// F4 Report 14: Overall Project Timeline.
// Two filters (OR-joined): handover-date range, final-fitting-end range.

import React, { useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchReportTimeline } from "../../api/service";
import ReportShell, { display, downloadReportCsv } from "./ReportShell";

const TimelineReport = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ ho_from: "", ho_to: "", ff_from: "", ff_to: "" });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasRun, setHasRun] = useState(false);

  const apply = async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filters.ho_from) params.ho_from = filters.ho_from;
      if (filters.ho_to)   params.ho_to   = filters.ho_to;
      if (filters.ff_from) params.ff_from = filters.ff_from;
      if (filters.ff_to)   params.ff_to   = filters.ff_to;
      const res = await fetchReportTimeline(params);
      setRows(res?.rows || []);
      setCount(res?.count || 0);
      setHasRun(true);
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFilters({ ho_from: "", ho_to: "", ff_from: "", ff_to: "" });
    setRows([]); setCount(0); setHasRun(false); setError(null);
  };

  const exportCsv = () => {
    const qs = new URLSearchParams();
    if (filters.ho_from) qs.append("ho_from", filters.ho_from);
    if (filters.ho_to)   qs.append("ho_to",   filters.ho_to);
    if (filters.ff_from) qs.append("ff_from", filters.ff_from);
    if (filters.ff_to)   qs.append("ff_to",   filters.ff_to);
    const tail = qs.toString();
    downloadReportCsv("timeline", tail ? `?${tail}` : "");
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <button
            className="btn btn-link p-0 mb-1"
            onClick={() => navigate("/reports")}
            style={{ textDecoration: "none" }}
          >
            ← Back to Reports
          </button>
          <h4 className="mb-0">14. Overall Project Timeline</h4>
          <small className="text-muted">Days from sales handover to final fitting completion</small>
        </div>
        {hasRun && (
          <Button variant="outline-primary" size="sm" onClick={exportCsv}>
            Export CSV
          </Button>
        )}
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <Row>
            <Col md={3}>
              <Form.Label>Handover from</Form.Label>
              <Form.Control
                type="date"
                value={filters.ho_from}
                onChange={(e) => setFilters((f) => ({ ...f, ho_from: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Handover to</Form.Label>
              <Form.Control
                type="date"
                value={filters.ho_to}
                onChange={(e) => setFilters((f) => ({ ...f, ho_to: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Final fitting end from</Form.Label>
              <Form.Control
                type="date"
                value={filters.ff_from}
                onChange={(e) => setFilters((f) => ({ ...f, ff_from: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Final fitting end to</Form.Label>
              <Form.Control
                type="date"
                value={filters.ff_to}
                onChange={(e) => setFilters((f) => ({ ...f, ff_to: e.target.value }))}
              />
            </Col>
          </Row>
          <div className="mt-3 d-flex gap-2">
            <Button variant="primary" onClick={apply} disabled={loading}>
              Apply filters
            </Button>
            <Button variant="outline-secondary" onClick={reset} disabled={loading}>
              Reset
            </Button>
            <small className="text-muted ms-auto align-self-center">
              Filters are OR-joined — a project matches if it falls in either range.
            </small>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {!loading && hasRun && !error && (
        <>
          <div className="mb-2 text-muted small">
            {count} {count === 1 ? "project" : "projects"} matched
          </div>
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th className="text-center">Project</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Handover Date</th>
                  <th className="text-center">Final Fitting End</th>
                  <th className="text-center">Days</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-muted py-4">No matches.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.project_id}>
                    <td className="text-center">{r.project_label}</td>
                    <td className="text-center">{display(r.project_status)}</td>
                    <td className="text-center">{display(r.ho_date)}</td>
                    <td className="text-center">{display(r.final_fitting_end)}</td>
                    <td className="text-center">
                      {typeof r.days === "number" ? r.days : <span className="text-muted">{r.days}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TimelineReport;
